import { FC, useEffect, useMemo, useContext } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Stack,
  FormControlLabel,
  Switch,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { AuthContext } from 'components/authentication/context/authContext';
import intl from 'components/i18n/ReactIntlWrapper';
import {
  useCreateAccount,
  useUpdateAccount,
  useCurrencies,
  useAccountTypes,
  useRoutingTypes,
  AccountTypeData,
  RoutingTypeData,
} from 'store/registry/useAccountManagement';
import {
  Account,
  AccountFormData,
} from 'types/account';
import {
  validateAccountNumber,
  validateAccountName,
  validateDescription,
} from 'utils/account-validators';
import { useCurrencyInput } from 'hooks/useCurrencyInput';
import { CorporateEntityAutocomplete, PersonaAutocomplete } from './Selects';

type AccountFormDrawerProps = {
  open: boolean;
  account: Account | null;
  onClose: () => void;
  onSuccess: () => void;
  readOnly?: boolean;
};

const AccountFormDrawer: FC<AccountFormDrawerProps> = ({ open, account, onClose, onSuccess, readOnly = false }) => {
  const toast = useToastMessageQueue();
  const queryClient = useQueryClient();
  const isEditMode = !!account;
  const disabled = readOnly || isEditMode; // Centralized disabled logic for form fields
  const { userEntityDetails } = useContext(AuthContext);

  const createMutation = useCreateAccount();
  const updateMutation = useUpdateAccount();
  const { data: currencies = [] } = useCurrencies();
  const { data: accountTypes = [] } = useAccountTypes();
  const { data: routingTypes = [] } = useRoutingTypes();
  
  // Currency input hook for opening balance
  const currencyInput = useCurrencyInput(0);
  
  // Get the user's corporate entity ID from their entity
  const userCorporateEntityId = userEntityDetails?.entityId;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<AccountFormData>({
    defaultValues: {
      accountNumber: '',
      accountName: '',
      accountTypeId: accountTypes?.[0]?.id || 1, // Use first account type ID as default
      description: '',
      idCurrency: currencies.find(c => c.code === 'USD')?.id || 1,
      bank: '',
      routingNumber: '',
      routingTypeId: routingTypes?.[0]?.id || undefined,
      openingBalance: 0,
      isActive: true,
      corporateEntityId: userCorporateEntityId,
      personaId: undefined,
    },
  });

  const selectedAccountTypeId = watch('accountTypeId');
  const selectedCorporateEntity = watch('corporateEntityId');

  // Reset form when drawer opens/closes or account changes
  useEffect(() => {
    if (open) {
      if (account) {
        // Find currency ID from account currency code
        const currencyId = currencies.find(c => c.code === account.currency)?.id || 1;
        // Find account type ID from existing account or use first available type as fallback
        const accountTypeId = account.accountTypeId || accountTypes?.[0]?.id || 1;
        reset({
          accountNumber: account.accountNumber,
          accountName: account.accountName,
          accountTypeId: accountTypeId,
          description: account.description || '',
          idCurrency: currencyId,
          bank: account.bank || '',
          routingNumber: account.routingNumber || '',
          routingTypeId: account.routingTypeId || undefined,
          openingBalance: 0, // Opening balance only for creation
          isActive: account.isActive,
          corporateEntityId: account.corporateEntityId,
          personaId: account.personaId,
        });
        // Reset currency input for edit mode (no opening balance)
        currencyInput.setValue(0);
      } else {
        // Auto-fill corporateEntityId from user context when creating new account
        reset({
          accountNumber: '',
          accountName: '',
          accountTypeId: accountTypes?.[0]?.id || 1, // Use first account type as default
          description: '',
          idCurrency: currencies.find(c => c.code === 'USD')?.id || 1,
          bank: '',
          routingNumber: '',
          routingTypeId: routingTypes?.[0]?.id || undefined,
          openingBalance: 0,
          isActive: true,
          corporateEntityId: userCorporateEntityId,
          personaId: undefined,
        });
        // Reset currency input for new account
        currencyInput.setValue(0);
      }
    }
  }, [open, account, reset, userCorporateEntityId, currencyInput.setValue, accountTypes]);

  // Determine if certain fields should be shown based on account type
  // Corporate Entity is always required for all account types
  const showCorporateEntity = true;

  const RECEIVABLES_ACCOUNT_TYPE_IDS = [6, 7]; 
  const PAYABLES_ACCOUNT_TYPE_IDS = [8, 9, 10]; 
  const CASH_AND_BANKS_ACCOUNT_TYPE_IDS = [1, 2, 3, 4, 5]; 
  const LOANS_AND_LIABILITIES_ACCOUNT_TYPE_IDS = [11, 12, 13]; 

  const showPersona = useMemo(() => {
    // Show persona fields for Receivables (clients) and Payables (suppliers) account types
    return selectedAccountTypeId ? (
      RECEIVABLES_ACCOUNT_TYPE_IDS.includes(selectedAccountTypeId) ||
      PAYABLES_ACCOUNT_TYPE_IDS.includes(selectedAccountTypeId)
    ) : false;
  }, [selectedAccountTypeId]);

  const showBankingFields = useMemo(() => {
    // Show banking fields (Bank, Routing Number, Routing Type) for bank-related account types
    return selectedAccountTypeId ? (
      CASH_AND_BANKS_ACCOUNT_TYPE_IDS.includes(selectedAccountTypeId) ||
      LOANS_AND_LIABILITIES_ACCOUNT_TYPE_IDS.includes(selectedAccountTypeId) ||
      RECEIVABLES_ACCOUNT_TYPE_IDS.includes(selectedAccountTypeId) ||
      PAYABLES_ACCOUNT_TYPE_IDS.includes(selectedAccountTypeId)
    ) : false;
  }, [selectedAccountTypeId]);

  const onSubmit = async (data: AccountFormData) => {
    try {
      if (isEditMode && account) {
        await updateMutation.mutateAsync({
          id: account.id,
          data,
        });
      } else {
        // For new accounts, use the currency input value (in cents)
        const formDataWithCurrencyBalance = {
          ...data,
          openingBalance: currencyInput.centValue, // Use cents value from currency input
        };
        await createMutation.mutateAsync(formDataWithCurrencyBalance);
      }
      onSuccess();
    } catch (error) {
      // Error is handled by mutation's onError callback
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 500 }, p: 3 },
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* Header */}
        <Typography variant="h5" gutterBottom>
          {readOnly 
            ? intl.get('accountManagement.accountDetails')
            : isEditMode 
              ? intl.get('accountManagement.editAccount')
              : intl.get('accountManagement.createAccount')
          }
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {readOnly 
            ? intl.get('accountManagement.viewAccountInfo')
            : isEditMode 
              ? intl.get('accountManagement.updateAccountInfo')
              : intl.get('accountManagement.createAccountDescription')
          }
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Form Fields */}
        <Stack spacing={3}>
          {/* Account Number */}
          <Controller
            name="accountNumber"
            control={control}
            rules={{
              validate: validateAccountNumber,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={intl.get('accountManagement.accountNumber')}
                required
                fullWidth
                error={!!errors.accountNumber}
                helperText={errors.accountNumber?.message}
                disabled={disabled} // Don't allow changing account number in edit or read-only mode
              />
            )}
          />

          {/* Account Name */}
          <Controller
            name="accountName"
            control={control}
            rules={{
              validate: validateAccountName,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={intl.get('accountManagement.accountName')}
                required
                fullWidth
                error={!!errors.accountName}
                helperText={errors.accountName?.message}
                disabled={readOnly}
              />
            )}
          />

          {/* Account Type */}
          <Controller
            name="accountTypeId"
            control={control}
            rules={{ required: intl.get('accountManagement.accountTypeRequired') }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label={intl.get('accountManagement.accountType')}
                required
                fullWidth
                error={!!errors.accountTypeId}
                helperText={errors.accountTypeId?.message}
                disabled={disabled} // Readonly in edit mode for data integrity
              >
                {accountTypes?.map((accountType: AccountTypeData) => (
                  <MenuItem key={accountType.id} value={accountType.id}>
                    {accountType.displayName || accountType.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {/* Currency */}
          <Controller
            name="idCurrency"
            control={control}
            rules={{ required: intl.get('accountManagement.currencyRequired') }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label={intl.get('accountManagement.currency')}
                required
                fullWidth
                disabled={disabled}
                error={!!errors.idCurrency}
                helperText={errors.idCurrency?.message}
              >
                {currencies?.map((currency) => (
                  <MenuItem key={currency.id} value={currency.id}>
                    {currency.code} - {currency.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />

          {/* Banking Fields - Conditional */}
          {showBankingFields && (
            <>
              {/* Bank Name */}
              <Controller
                name="bank"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={intl.get('accountManagement.bankName')}
                    fullWidth
                    disabled={disabled} // Readonly in edit mode for banking integrity
                    helperText={intl.get('accountManagement.bankNameHelper')}
                  />
                )}
              />

              {/* Routing Number */}
              <Controller
                name="routingNumber"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={intl.get('accountManagement.routingNumber')}
                    fullWidth
                    disabled={disabled} // Readonly in edit mode for banking integrity
                    helperText={intl.get('accountManagement.routingNumberHelper')}
                  />
                )}
              />

              {/* Routing Type */}
              <Controller
                name="routingTypeId"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    label={intl.get('accountManagement.routingType')}
                    fullWidth
                    disabled={disabled} // Readonly in edit mode for banking integrity
                    helperText={intl.get('accountManagement.routingTypeHelper')}
                  >
                    <MenuItem value="">
                      <em>{intl.get('common.none')}</em>
                    </MenuItem>
                    {routingTypes?.map((routingType: RoutingTypeData) => (
                      <MenuItem key={routingType.id} value={routingType.id}>
                        {routingType.displayName || routingType.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </>
          )}

          {/* Opening Balance - Only for new accounts */}
          {!isEditMode && (
            <TextField
              label={intl.get('accountManagement.openingBalance')}
              fullWidth
              disabled={readOnly}
              inputProps={{ 
                style: { textAlign: 'right' }
              }}
              helperText={intl.get('accountManagement.openingBalance')}
              value={currencyInput.displayValue}
              onChange={(e) => currencyInput.handleChange(e.target.value)}
              placeholder="0.00"
            />
          )}

          {/* Description */}
          <Controller
            name="description"
            control={control}
            rules={{
              validate: validateDescription,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label={intl.get('accountManagement.description')}
                fullWidth
                multiline
                rows={3}
                disabled={readOnly}
                error={!!errors.description}
                helperText={errors.description?.message || intl.get('accountManagement.descriptionHelp')}
              />
            )}
          />

          {/* Corporate Entity - Conditional */}
          {showCorporateEntity && (
            <Controller
              name="corporateEntityId"
              control={control}
              render={({ field }) => (
                <CorporateEntityAutocomplete
                  value={field.value}
                  onChange={field.onChange}
                  label={intl.get('accountManagement.corporateEntity')}
                  helperText={intl.get('accountManagement.corporateEntityHelp')}
                  disabled={true}
                />
              )}
            />
          )}

          {/* Persona - Conditional */}
          {showPersona && (
            <Controller
              name="personaId"
              control={control}
              render={({ field }) => (
                <PersonaAutocomplete
                  value={field.value}
                  onChange={field.onChange}
                  label={intl.get('accountManagement.persona')}
                  helperText={intl.get('accountManagement.personaHelp')}
                  entityTypes={[2, 3]} // 2 = Client, 3 = Provider
                  disabled={readOnly}
                />
              )}
            />
          )}

          {/* Active Status */}
          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => field.onChange(e.target.checked)}
                    disabled={readOnly}
                  />
                }
                label={intl.get('accountManagement.active')}
              />
            )}
          />

          {isEditMode && !watch('isActive') && (
            <Alert severity="warning">
              {intl.get('accountManagement.setInactive')}
            </Alert>
          )}
        </Stack>

        <Divider sx={{ my: 3 }} />

        {/* Action Buttons */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {readOnly 
              ? intl.get('common.close')
              : intl.get('common.cancel')
            }
          </Button>
          {!readOnly && (
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={isSubmitting && <CircularProgress size={16} />}
            >
              {isSubmitting 
                ? intl.get('common.saving')
                : isEditMode 
                  ? intl.get('accountManagement.updateAccount')
                  : intl.get('accountManagement.createAccount')
              }
            </Button>
          )}
        </Stack>
      </Box>
    </Drawer>
  );
};

export default AccountFormDrawer;
